import {
  ssoLogin,
  ssoRegisterIncomplete,
  checkEmailAvailability,
} from '@client/new/api/sso.api';
import { useUserStore } from '@client/new/stores/user.store';
import { useSsoSessionStore } from '@client/new/stores/sso-session.store';
import { useCallback, useRef, useState } from 'react';
import { navigate } from '@client/routes';
import axios from 'axios';
import { trackSignInWithSso } from './register.effects';

type AppleSsoState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'error'; message: string };

const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_SSO_CLIENT_ID ?? '';
const APPLE_REDIRECT_URI =
  import.meta.env.VITE_APPLE_SSO_REDIRECT_URI ?? window.location.origin;

export const useAppleSso = (mode: 'login' | 'signup' = 'login') => {
  const [state, setState] = useState<AppleSsoState>({ type: 'idle' });
  const processingRef = useRef(false);

  const initiateAppleSignIn = useCallback(async () => {
    if (!window.AppleID?.auth) {
      setState({
        type: 'error',
        message: 'Apple Sign-In is not available. Please try again later.',
      });
      return;
    }

    if (!APPLE_CLIENT_ID) {
      setState({ type: 'error', message: 'Apple Sign-In is not configured.' });
      return;
    }

    if (processingRef.current) {
      return;
    }

    processingRef.current = true;
    setState({ type: 'loading' });

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization.id_token;
      const appleFirstName = response.user?.name?.firstName;
      const appleLastName = response.user?.name?.lastName;

      if (mode === 'signup') {
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          const email = payload.email;
          if (email) {
            const isAvailable = await checkEmailAvailability(email);
            if (!isAvailable) {
              const result = await ssoLogin({
                provider: 'apple',
                idToken,
              });
              const user = result?.user ?? result;
              trackSignInWithSso(user, 'apple');
              useUserStore.getState().login(user);
              navigate('/');
              return;
            }
          }
        } catch {
          // availability check or login failed — fall through to registration
        }

        if (appleFirstName || appleLastName) {
          await ssoRegisterIncomplete({
            provider: 'apple',
            idToken,
            firstName: appleFirstName,
            lastName: appleLastName,
          }).catch(() => undefined);
        }

        useSsoSessionStore
          .getState()
          .setSsoSession('apple', idToken, appleFirstName, appleLastName);
        setState({ type: 'idle' });
        return;
      }

      try {
        const result = await ssoLogin({
          provider: 'apple',
          idToken,
        });

        const user = result?.user ?? result;
        trackSignInWithSso(user, 'apple');
        useUserStore.getState().login(user);
        navigate('/');
        return;
      } catch (loginError) {
        if (
          !axios.isAxiosError(loginError) ||
          loginError.response?.status !== 404
        ) {
          throw loginError;
        }
      }

      if (appleFirstName || appleLastName) {
        await ssoRegisterIncomplete({
          provider: 'apple',
          idToken,
          firstName: appleFirstName,
          lastName: appleLastName,
        }).catch(() => undefined);
      }

      useSsoSessionStore
        .getState()
        .setSsoSession('apple', idToken, appleFirstName, appleLastName);
      navigate('/signup');
      setState({ type: 'idle' });
    } catch (error: unknown) {
      const isUserCancelled =
        error &&
        typeof error === 'object' &&
        'error' in error &&
        (error as { error: string }).error === 'popup_closed_by_user';

      if (isUserCancelled) {
        setState({ type: 'idle' });
        return;
      }

      setState({
        type: 'error',
        message: 'Apple sign-in failed. Please try again.',
      });
    } finally {
      processingRef.current = false;
    }
  }, []);

  return {
    state,
    initiateAppleSignIn,
  };
};
